// Hook global de branding — qualquer app pode importar e ler/aplicar.
// O config vem de /api/public/branding (não-autenticado).
//
// Uso:
//   import { brandingSignal, loadBranding } from "@app/ui/lib/branding";
//   loadBranding();  // chama uma vez no boot do app
//   const b = brandingSignal.value;  // reactive read
//
// Aplicar no DOM (título, favicon, --primary CSS var):
//   import { applyBranding } from "@app/ui/lib/branding";
//   applyBranding(brandingSignal.value);
//
// Atualizações: o admin que edita via /manager/ chama refreshBranding()
// e o signal global propaga pra todos os componentes que estão observando.

import { signal } from "@preact/signals";

export interface BrandingPortalLabels {
  student: string;
  teacher: string;
  company: string;
  manager: string;
  auth: string;
}

export interface Branding {
  platformName: string;
  faviconUrl: string | null;
  logoUrl: string | null;
  primaryColor: string;
  portalLabels: BrandingPortalLabels;
  loginSubtitle: string | null;
  footerText: string | null;
  updatedAt: number | null;
}

const DEFAULT: Branding = {
  platformName: "Simulador",
  faviconUrl: null,
  logoUrl: null,
  primaryColor: "#8b5cf6",
  portalLabels: {
    student: "Aluno",
    teacher: "Professor",
    company: "Organização",
    manager: "Administrador",
    auth: "Entrar"
  },
  loginSubtitle: "Estude, simule provas e acompanhe sua evolução.",
  footerText: null,
  updatedAt: null
};

export const brandingSignal = signal<Branding>(DEFAULT);
let loaded = false;
let inFlight: Promise<void> | null = null;

export async function loadBranding(opts: { force?: boolean } = {}): Promise<void> {
  if (loaded && !opts.force) return;
  if (inFlight && !opts.force) return inFlight;
  inFlight = (async () => {
    try {
      const res = await fetch("/api/public/branding", {
        credentials: "include",
        cache: opts.force ? "no-store" : "default"
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { config?: Partial<Branding> };
      const next = { ...DEFAULT, ...(data.config ?? {}) } as Branding;
      next.portalLabels = {
        ...DEFAULT.portalLabels,
        ...(data.config?.portalLabels ?? {})
      };
      brandingSignal.value = next;
      loaded = true;
      applyBranding(next);
    } catch (err) {
      console.warn("[branding] falha ao carregar, mantendo defaults:", err);
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

export function refreshBranding(): Promise<void> {
  return loadBranding({ force: true });
}

// Aplica no DOM: <title>, favicon, e --primary CSS var.
export function applyBranding(b: Branding): void {
  if (typeof document === "undefined") return;
  try {
    document.title = b.platformName;
  } catch {
    /* SSR */
  }
  if (b.faviconUrl) {
    const link = document.getElementById("favicon-link") as HTMLLinkElement | null;
    if (link) link.href = b.faviconUrl;
  }
  if (b.primaryColor) {
    document.documentElement.style.setProperty("--brand-primary", b.primaryColor);
  }
}

// Helper pra views: nome do portal atual de acordo com URL prefix.
export function getCurrentPortalLabel(): string {
  if (typeof window === "undefined") return brandingSignal.value.platformName;
  const path = window.location.pathname;
  const labels = brandingSignal.value.portalLabels;
  if (path.startsWith("/auth")) return labels.auth;
  if (path.startsWith("/student")) return labels.student;
  if (path.startsWith("/teacher")) return labels.teacher;
  if (path.startsWith("/company")) return labels.company;
  if (path.startsWith("/manager")) return labels.manager;
  return brandingSignal.value.platformName;
}
